/**
 * The two bottom panels (Organisational Readiness, AI Readiness) and the
 * Digital Intensity Index card. A port of app/public/dashboard.js; see
 * charts.jsx for why inline style property order is kept.
 */

import {
  BAND, CAPABILITIES, READINESS_COPY, DII_BANDS, DII_VARIABLES,
  diiBand, pctOf, comparisonAbsenceReason,
} from './constants.js';

export function Readiness({ report }) {
  return (
    <div className="panel" style={{ animation: 'panelIn .5s cubic-bezier(.16,1,.3,1) 1.6s both' }}>
      <div className="panel-title">Organisational Readiness</div>
      <div className="panel-sub">Reported separately — never added to the maturity score.</div>
      <div className="readiness-list">
        {['leadership', 'team'].map((key, i) => {
          const score = report.orgReadiness.scores[key];
          const bandName = report.orgReadiness.bands[key];
          if (score === null) return null;
          const band = BAND[bandName];
          return (
            <div key={key} style={{ animation: `rowIn .4s cubic-bezier(.16,1,.3,1) ${(1.7 + i * 0.12).toFixed(2)}s both` }}>
              <div className="readiness-row">
                {/* Canonical labels come from the engine, never hard-coded. */}
                <div className="readiness-label">{report.orgReadiness.labels[key]}</div>
                <div className="readiness-band" style={{ color: band.edge }}>{bandName}</div>
              </div>
              <div className="readiness-track">
                <div className="readiness-marker" style={{ left: `${pctOf(score)}%`, background: band.chart }} />
              </div>
              <div className="readiness-body">{READINESS_COPY[key][bandName]}</div>
            </div>
          );
        })}
      </div>
      {report.riskFlags?.length > 0 && (
        <div className="flag-note">
          <strong>Priority support: </strong>
          {report.riskFlags[0].message}
        </div>
      )}
    </div>
  );
}

export function AiReadiness({ report, benchmark }) {
  const cohort = benchmark?.cohort;
  return (
    <div className="panel" style={{ animation: 'panelIn .5s cubic-bezier(.16,1,.3,1) 1.75s both' }}>
      <div className="panel-title">Your AI Readiness</div>
      <div className="panel-sub">Six capability areas, each scored and given its own digital maturity level — not one blended AI figure.</div>
      <div className="ai-list">
        {CAPABILITIES.map(([key, label], i) => {
          const score = report.scores.d5Breakdown[key];
          const bandName = report.d5CapabilityBands[key];
          if (score === null) return null;
          const band = BAND[bandName];
          // Each capability gets its OWN cohort average, not one D5 figure
          // repeated six times. D5 sits in the common trunk, so the
          // comparison is every business assessed rather than the peer group.
          const cohortScore = cohort?.d5Breakdown?.[key];
          return (
            <div key={key} style={{ animation: `rowIn .4s cubic-bezier(.16,1,.3,1) ${(1.85 + i * 0.07).toFixed(2)}s both` }}>
              <div className="ai-row">
                <div className="ai-label">{label}</div>
                <div className="ai-band" style={{ color: band.edge }}>{bandName}</div>
              </div>
              <div className="ai-track">
                <div
                  className="ai-fill"
                  style={{
                    width: `${pctOf(score)}%`,
                    background: band.bar,
                    animation: `barGrow .5s cubic-bezier(.16,1,.3,1) ${(1.95 + i * 0.07).toFixed(2)}s both`,
                  }}
                />
                {cohortScore !== null && cohortScore !== undefined && (
                  <div
                    className="ai-tick"
                    style={{ left: `${pctOf(cohortScore)}%`, animation: `scoreReveal .3s ease-out ${(2.1 + i * 0.07).toFixed(2)}s both` }}
                    title={`Average across all businesses: ${cohortScore.toFixed(1)}`}
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>
      {cohort && cohort.count > 1 ? (
        <div className="ai-legend" style={{ animation: 'scoreReveal .4s ease-out 2.4s both' }}>
          <span className="ai-legend-tick" />
          {` Average of all ${cohort.count} businesses assessed`}
        </div>
      ) : (
        // The ticks are a sector comparison too, so they go with the rest of
        // it. Without a word here the six bars just look like they lost a feature.
        <div className="absence-note">{comparisonAbsenceReason(report, benchmark)}</div>
      )}
    </div>
  );
}

export function Dii({ report, benchmark }) {
  const dii = report.dii;
  if (!dii) return null;
  const band = diiBand(dii.score);
  const average = benchmark?.cohort?.diiAverage;

  return (
    // Its own tab, so it animates on arrival rather than waiting out the
    // maturity view's entry sequence.
    <div className="dii-card" style={{ animation: 'panelIn .45s cubic-bezier(.16,1,.3,1) .05s both' }}>
      <div className="dii-head">
        <div>
          <div className="eyebrow">National reporting</div>
          <h2 className="dii-title">Digital Intensity Index</h2>
          <p className="dii-intro">
            {"Where your business sits on the EU's shared Digital Intensity Index scale — "
              + "and how that feeds Fáilte Ireland's national reporting."}
          </p>
        </div>
        <div className="dii-score-box">
          <div className="dii-score">
            <span className="dii-score-value" style={{ color: band.edge }}>{String(dii.score)}</span>
            <span className="dii-score-of">/ 12</span>
          </div>
          <span className="band-pill dii-pill" style={{ background: band.fill, color: band.on }}>{band.band}</span>
        </div>
      </div>

      {/* The whole scale, always — the business sees where it sits relative
          to every band, not just its own. */}
      <div className="dii-scale">
        {DII_BANDS.map((b) => (
          <div key={b.band} className={`dii-seg${b.band === band.band ? ' current' : ''}`} style={{ background: b.fill }}>
            <span className="dii-seg-label">{b.band}</span>
            <span className="dii-seg-range">{`${b.min}–${b.max}`}</span>
          </div>
        ))}
      </div>

      <div className="dii-grid">
        {DII_VARIABLES.map((variable) => {
          const on = dii[variable.key] === true;
          return (
            <div key={variable.key} className={`dii-cell${on ? ' on' : ''}`}>
              <span className={`dii-dot${on ? ' on' : ''}`} />
              <span className="dii-cell-text">
                <span className="dii-cell-label">{variable.label}</span>
                {/* Three of the twelve are inferred from maturity answers
                    rather than asked again (v13 derivation note); saying so
                    avoids a reader hunting for a question never put to them. */}
                <span className="dii-cell-source">{variable.derived ? `derived from ${variable.source}` : variable.source}</span>
              </span>
            </div>
          );
        })}
      </div>

      <div className="dii-foot">
        {average !== null && average !== undefined && (
          <p className="dii-cohort">
            <span className="dii-cohort-label">Average of all businesses assessed: </span>
            <strong>{`${average.toFixed(1)} / 12`}</strong>
            <span className="dii-cohort-note">{` (n=${benchmark.cohort.count})`}</span>
          </p>
        )}
        <p className="dii-disclosure">
          {"This score is also shared, in aggregate, as part of Fáilte Ireland's reporting to the "
            + 'Department (DETE) — helping track tourism digitalisation nationally, never singling out '
            + 'your business.'}
        </p>
      </div>
    </div>
  );
}
